import React, {
  ChangeEvent,
  ComponentType,
  FC,
  LazyExoticComponent,
  ReactNode,
  Suspense,
} from 'react';

import { Route, To } from 'react-router-dom';
import { AxiosError, AxiosResponseHeaders } from 'axios';
import { FormikErrors, FormikTouched } from 'formik';
import { get, has, omit } from 'lodash';
import { DateTime, DateTimeFormatOptions } from 'luxon';
import i18n from '@app/i18n';

import {
  BrowserStorage,
  DateFormat,
  DraftEnvelopePrepositionedMarkType,
  Instance,
  Languages,
  LocalStorageKeysForSaveBetweenSessions,
  Space,
  BASE_URL,
  PER_PAGE_ITEMS_DEFAULT,
  apiUrlCookieName,
  EnvelopeFlowState,
  EnvelopeAllovedActions,
  FlowStageState,
  TasksStatus,
  MassiveActionPage,
  PhoneVerifyCountry,
} from '@const/consts'

import { paginationInitialState } from '@store/modules/pagination/consts';

import {
  ILibDropdownItem,
  ILibOption,
  ILibPagination,
  ILibSelectValueType,
  Loader,
  Nullable,
  showBasicNotification,
  showConfirmNotification,
  showErrorNotification,
  showSuccessNotification,
} from '@infologistics/frontend-libraries';
import { Instance as flInstance } from '@infologistics/frontend-libraries'

import { IRoute } from '@common/Routing/types';
import {
  AllowedAction,
  IBasicNotificationOptions,
  IConfirmNotificationOptions,
  IName,
  ISuccessNotificationOptions,
  ITo,
} from './types';
import { IParams, SelectChangeFunction } from '@app/types';
import {
  ICurrentEnvelope,
  IDraftEnvelope,
  IDraftEnvelopeData,
  IDraftEnvelopePrepositionedMarkData,
  IDraftEnvelopeRecipientData,
  IPreviewDimensions,
} from '@store/modules/envelopes/types';
import store from '@store/configureStore';
import { setLanguage } from '@store/modules/utils';
import { Buffer } from 'buffer';
import { TFunction } from 'react-i18next';
import { IBoxRole } from '@app/store/modules/boxes/types';
import queryString from 'query-string'
import cookies from './cookies'

export const displaySuccessNotification = (
  options: ISuccessNotificationOptions
): void => {
  const { content, title } = options;
  const { language, getResource } = i18n;

  showSuccessNotification({
    content:
      content ??
      String(getResource(language, 'notification', 'successContent')),
    title:
      title ?? String(getResource(language, 'notification', 'successTitle')),
  });
};

export const displayBasicNotification = (
  options: IBasicNotificationOptions
): void => {
  const { content, title, type } = options;
  const { language, getResource } = i18n;

  showBasicNotification({
    content: content || getResource(language, 'notification', 'successContent'),
    title: title || getResource(language, 'notification', 'successTitle'),
    type,
  });
};

export const displayErrorNotification = ({
  response,
}: AxiosError<any>): void => {
  const { language, getResource } = i18n;

  const errorMessage = String(
    getResource(language, 'notification', 'errorContent')
  );

  showErrorNotification({
    content: response?.data?.message || errorMessage,
    title: '',
  });
};

export const displayThunkErrorNotification = (
  data: Record<string, any> | undefined
): void => {
  const { language, getResource } = i18n;

  const errorMessage = String(
    getResource(language, 'notification', 'errorContent')
  );

  showErrorNotification({
    content: data?.message || errorMessage,
    title: '',
  });
};

export const displayConfirmNotification = (
  options: IConfirmNotificationOptions
): void => {
  const {
    cancelText,
    submitText,
    content,
    contentNotification,
    title,
    onSubmit,
    theme,
  } = options;
  const { language, getResource } = i18n;

  showConfirmNotification({
    cancelText: cancelText ?? getResource(language, 'common', 'no'),
    content: content ?? getResource(language, 'notification', 'confirmContent'),
    contentNotification,
    onSubmit,
    submitText: submitText ?? getResource(language, 'common', 'yes'),
    title: title ?? getResource(language, 'notification', 'confirmTitle'),
    theme: theme ?? 'warning',
  });
};

export const isAxiosError = (candidate: any): candidate is AxiosError =>
  candidate.isAxiosError === true;

export const isObject = (candidate: any): candidate is Record<string, any> =>
  candidate !== null && typeof candidate === 'object';

export const getRoutesList = (routes: IRoute[]): ReactNode[] =>
  routes.map((route: IRoute) => {
    const { name, path, routeAdditionalProp } = route;

    return (
      <Route
        key={name}
        path={path}
        element={<route.element routeAdditionalProp={routeAdditionalProp} />}
      />
    );
  });

export const createUrl = (...urlData: Array<string | number>): string =>
  urlData.reduce((acc: string, currentValue: string | number) => {
    if (!currentValue) return acc;

    const newCurrentValue =
      typeof currentValue !== 'number' &&
      !currentValue.includes('/') &&
      acc.slice(-1) !== '/'
        ? `/${currentValue}`
        : currentValue;
    return `${acc}${newCurrentValue}`;
  }, '');

export const transformPathToString = (to: To): string => {
  const newTo = '';

  if (typeof to === 'string') return newTo + to;
  if (typeof to === 'object' && to.pathname) return to.pathname;

  return newTo;
};

export const getLazyComponent =
  (ComponentLazy: LazyExoticComponent<ComponentType<any>>): FC =>
  (props) =>
    (
      <Suspense fallback={<Loader loading />}>
        <ComponentLazy {...props} />
      </Suspense>
    );

export const hasError = (
  name: string,
  errors?: FormikErrors<any>,
  touched?: FormikTouched<any>,
  /** Отключение проверки touched */
  noTouched?: boolean
): boolean => has(errors, name) && (has(touched, name) || !!noTouched);

export const getErrorText = (
  name: string,
  errors?: FormikErrors<any>,
  /** Отключение вывода при равенстве определенному значению */
  disableForText?: string
): string =>
  has(errors, name)
    ? disableForText
      ? String(get(errors, name)) !== disableForText
        ? String(get(errors, name))
        : ''
      : String(get(errors, name))
    : '';

export const clearLocalStorage = (): void => {
  const keysToClear = Object.keys(localStorage).filter(
    (key) => !LocalStorageKeysForSaveBetweenSessions.includes(key)
  );

  keysToClear.forEach((key) => {
    localStorage.removeItem(key);
  });
};

export const catchAxiosError = (
  err: unknown,
  rejectWithValue: (value: unknown) => void
): void => {
  if (!isAxiosError(err)) return;

  return rejectWithValue(err.response?.data);
};

export const catchThunkError = (data: unknown): void => {
  if (!isObject(data)) return;

  displayThunkErrorNotification(data);
};

export const getLanguageName = (locale: string): string => {
  const localeName = locale.substring(0, 2);

  return Object.values(Languages).includes(localeName)
    ? localeName
    : Languages.EN;
};

export const selectChangeCheck =
  (cb: any) =>
  (option: ILibSelectValueType): SelectChangeFunction | undefined =>
    option && !Array.isArray(option) ? cb(option) : undefined;

export const getDateFormat = (value: string, format: DateFormat): string =>
  DateTime.fromISO(value).toFormat(format);

export const getLocaleDateFormat = (
  value: string,
  locale: string,
  format?: DateTimeFormatOptions
): string =>
  DateTime.fromISO(value)
    .setLocale(locale)
    .toLocaleString(format ?? DateTime.DATE_SHORT);

export const getPagination = (
  headers: AxiosResponseHeaders
): ILibPagination => {
  const itemsPerPage = headers['x-per-page'];
  const nextPage = headers['x-next-page'];
  const prevPage = headers['x-prev-page'];
  const pageIndex = headers['x-page'];
  const total = headers['x-total'];

  if (!itemsPerPage && !nextPage && !prevPage && !pageIndex && !total)
    return paginationInitialState;

  return {
    itemsPerPage: itemsPerPage ? +itemsPerPage : 25,
    nextPage: nextPage ? +nextPage : null,
    prevPage: prevPage ? +prevPage : null,
    pageIndex: pageIndex ? +pageIndex : 1,
    total: total ? +total : null,
  };
};

export const redirectBack = (to: ITo, force?: boolean): void => {
  const { navigate, section } = to;
  const isHistory = window.history.length > 2;

  if (isHistory && !force) return navigate(-1);

  const url = createUrl(section);
  return navigate(url);
};

export const convertBytesToKilobytes = (bytes: number): number =>
  +(bytes / 1000).toFixed(2);

export const getDraftEnvelopeData = (
  values: IDraftEnvelope,
  senderBoxOguid: string
): IDraftEnvelopeData => {
  const {
    fields: fieldsValues,
    fieldsIdList,
    files,
    message: {
      comment,
      expirationDateTime: expirationDateTimeValue,
      externalId,
      subject,
    },
    prepositionedMarks: prepositionedMarksValues,
    prepositionedMarksIdList,
    preview,
    recipients: recipientsValues,
    recipientsIdList,
  } = values;

  const expirationDateTime = expirationDateTimeValue
    ? DateTime.fromJSDate(expirationDateTimeValue)
        .startOf('day')
        .toISO({ suppressMilliseconds: true })
    : null;

  const sortedRecipients = {};

  recipientsIdList.forEach((id) => {
    const recipient = recipientsValues[id];
    const { groupNum, boxEmail, guestEmail } = recipient;

    const {
      signer: { phone, ...restSigner },
      ...restRecipientData
    } = omit(recipient, ['color', 'customParams', 'groupNum']);
    const recipientData = {
      ...restRecipientData,
      boxEmail: boxEmail.replace(/\s/g, ''),
      guestEmail: guestEmail ? guestEmail.replace(/\s/g, '') : null,
      signer: {
        ...restSigner,
        phone: phone ? +phone.replace(/\D/g, '') : null,
      },
    };
    const prepositionedMarks = prepositionedMarksIdList
      .filter((markId) => prepositionedMarksValues[markId].recipient === id)
      .reduce((acc: IDraftEnvelopePrepositionedMarkData[], markId) => {
        const prepositionedMarkData = omit(prepositionedMarksValues[markId], [
          'customParamId',
          'recipient',
        ]);

        const { fileIndex, pageNumber, type, topLeftX, topLeftY } =
          prepositionedMarkData;

        const { width, height } =
          preview[files[fileIndex]].data[pageNumber - 1].dimensions;

        const newMarkData =
          type === DraftEnvelopePrepositionedMarkType.CHECKBOX ||
          type === DraftEnvelopePrepositionedMarkType.FULLNAME ||
          type === DraftEnvelopePrepositionedMarkType.INITIALS ||
          type === DraftEnvelopePrepositionedMarkType.POSITION ||
          type === DraftEnvelopePrepositionedMarkType.STRING
            ? {
                ...prepositionedMarkData,
                topLeftX: +(
                  (((topLeftX / 100) * width + 4) * 100) /
                  width
                ).toFixed(4),
                topLeftY: +(
                  (((topLeftY / 100) * height + 4) * 100) /
                  height
                ).toFixed(4),
              }
            : prepositionedMarkData;

        return [...acc, newMarkData];
      }, []);

    recipientData.prepositionedMarks = prepositionedMarks;

    const { boxPhone, boxEmail: emailBox, ...recipientDataNew } = recipientData
    const boxPhoneFormatted = boxPhone?.replace(/\D/g, '');
    recipientDataNew.boxId = boxPhoneFormatted ?? emailBox;

    sortedRecipients[groupNum] = Array.isArray(sortedRecipients[groupNum])
      ? [...sortedRecipients[groupNum], recipientDataNew]
      : [recipientDataNew];
  });

  const flow = Object.values<IDraftEnvelopeRecipientData[]>(
    sortedRecipients
  ).map((recipients) => ({
    recipients,
  }));

  const fields: Record<string, string> = fieldsIdList.reduce((acc, id) => {
    const { key, value } = fieldsValues[id];

    return {
      ...acc,
      [key]: value,
    };
  }, {});

  return {
    comment,
    expirationDateTime,
    externalId,
    fields: fieldsIdList.length ? fields : null,
    files,
    flow,
    senderBoxOguid,
    subject,
  };
};

export const getMetadataSelectOptions = (
  object: Record<string, string>
): ILibOption[] =>
  Object.keys(object).map((value) => ({
    label: object[value],
    value,
  }));

export const getFullname = (name: IName): string => {
  const { lastName, firstName, middleName, language } = name;

  return language !== Languages.RU
    ? collectNameString(firstName, middleName, lastName)
    : collectNameString(lastName, firstName, middleName);
};

export const getInitials = (name: IName): string => {
  const { lastName, firstName, middleName, language } = name;

  const firstNameInitial = firstName ? firstName[0].toUpperCase() + '.' : '';
  const middleNameInitial = middleName ? middleName[0].toUpperCase() + '.' : '';

  return language !== Languages.RU
    ? collectNameString(firstNameInitial, middleNameInitial, lastName)
    : collectNameString(lastName, firstNameInitial, middleNameInitial);
};

const collectNameString = (
  left?: Nullable<string>,
  center?: Nullable<string>,
  right?: Nullable<string>
): string => {
  const space = Space.STANDARD;

  return `${left ? left + space : ''}${center ? center + space : ''}${
    right ?? ''
  }`;
};

/** Функция изменения полного имени и инициалов при изменении ФИО */
export const handleFullnameChange = (
  e: ChangeEvent<HTMLInputElement>,
  field: string,
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
  },
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  language: string,
  values: any,
  changeInitials?: boolean
): void => {

  const handleNameChange = (
  ): void => {
    const value = e.target.value;

    const lastName = getNamePart(field, 'lastName', value, values['lastName']).trim();
    const firstName = getNamePart(field, 'firstName', value, values['firstName']).trim();
    const middleName = getNamePart(field, 'middleName', value, values['middleName']).trim();

    const name = {
      lastName,
      firstName,
      middleName,
      language
    }

    const fullName = getFullname(name)

    const initials = getInitials(name);

    handleChange(e);
    setFieldValue('fullName', fullName);
    changeInitials && setFieldValue('initials', initials);
  };

  field.includes('Name') && field !== 'fullName'
    ? handleNameChange()
    : handleChange(e);
};

const getNamePart = (
  field: string,
  fieldName: string,
  value: string,
  valueFieldName: any,
): string => (field === fieldName ? value : valueFieldName ?? '');

export const getImageDimensions = async (
  fileCode: string,
  mimeType: string
): Promise<IPreviewDimensions> => {
  const header = `data:${mimeType};base64`;
  const src = `${header}, ${fileCode}`;

  const img = new Image();
  img.src = src;
  await img.decode();

  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
  };
};

export const getInstance = (passedInstance?: string): Instance => {
  const instance =
    passedInstance ?? getInstance(store.getState().utils.instance);

  if (instance === Instance.BR) return Instance.BR;

  if (instance === Instance.RU) return Instance.RU;

  if (instance === Instance.ZA) return Instance.ZA;

  return Instance.GLOBAL;
};

/** Получение инстанса, совместимого с frontend-libraries */
export const getOldInstance = (instance: Instance): flInstance => {
  switch (instance) {
    case Instance.ZA:
      return flInstance.SOUTH_AFRICA;
    case Instance.BR:
      return flInstance.BRASIL;
    case Instance.RU:
      return flInstance.RUSSIAN;
    default:
      return flInstance.GLOBAL;
  }
}

export const getLanguageFromInstance = (locale: string) => {
  const instance = getInstance(store.getState().utils.instance);

  const isRussiaInstance = instance === Instance.RU;
  const isRussiaLocale = locale === Languages.RU;

  if (!locale) {
    switch (instance) {
      case Instance.RU:
        return Languages.RU;
      case Instance.BR:
        return Languages.PT;
      default:
        return Languages.EN;
    }
  }

  if (isRussiaLocale && isRussiaInstance) return Languages.RU;

  if (!isRussiaLocale) return locale;

  return Languages.EN;
};

export const getPreferredLanguages = (
  instance: Instance | string,
  locale: string
): string[] => {
  if (instance === Instance.RU || locale === Languages.RU)
    return ['ru', 'en', 'pt'];

  return ['en', 'pt'];
};

export const getPreferredCountries = (
  instance: Instance | string
): string[] => {
  if (instance === Instance.RU) return ['ru', 'us', 'br', 'za'];

  return ['us', 'br', 'za'];
};

export const getInitialCountryPhone = (instance: Instance | string): string => {
  const initialCountry = {
    [Instance.RU]: PhoneVerifyCountry.RU,
    [Instance.GLOBAL]: PhoneVerifyCountry.GLOBAL,
    [Instance.BR]: PhoneVerifyCountry.BR,
    [Instance.ZA]: PhoneVerifyCountry.ZA,
  };

  return initialCountry[instance] ?? PhoneVerifyCountry.GLOBAL;
};

export const getNameFormat = (
  firstName: string,
  lastName: string,
  middleName: Nullable<string>,
  isRU?: boolean
): string =>
  isRU
    ? `${lastName}${lastName && ' '}${firstName}${firstName && ' '}${
        middleName ?? ''
      }`
    : `${firstName}${firstName && ' '}${middleName ?? ''}${
        middleName ? ' ' : ''
      }${lastName}`;

export const setFormLanguage = (): void => {
  const locale =
    localStorage.getItem(BrowserStorage.LANGUAGE) ??
    getLanguageName(navigator.language);
  const language = getLanguageFromInstance(locale);

  if (i18n.language === language) return;

  i18n
    .changeLanguage(language)
    .then(() => {
      store.dispatch(setLanguage(language));
    })
    .catch(displayErrorNotification);
};

export const getBaseUrl = (): string => cookies.get(apiUrlCookieName) ?? BASE_URL

export function base64ToArrayBuffer(base64: string) {
  // const binaryString = atob(base64);
  const binaryString = Buffer.from(base64, 'base64').toString('utf8');

  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export const getDate = (
  value: string,
  language: string,
  textAt: string
): string =>
  `${getLocaleDateFormat(value, language)} ${textAt} ${getDateFormat(
    value,
    DateFormat.HOUR_AND_MINUTES
  )}`;

export const getPerPageNumber = (): string | number => {
  const itemPerPage = localStorage.getItem(window.location.pathname)

  return itemPerPage ?? PER_PAGE_ITEMS_DEFAULT
}

/** Получение параметров из search
 * @param {string} search строка поиска location.search
 * @param {number|false} [page = undefined] номер страницы. При undefined - подставляется page=1, при false - не добавляется
 */
export const getParamsFromSearch = (search: string, page?: number | string | false): any => {
  const params: any = queryString.parse(search)

  let paramsFinal = params;
  if (page !== false) {
    if (page) {
      paramsFinal.page = page
    } else if (!paramsFinal.page) {
      paramsFinal.page = 1
    }

    if (!paramsFinal.perPage) {
      paramsFinal.perPage = getPerPageNumber()
    }
  } else {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, perPage, ...others } = params
    paramsFinal = others;
  }

  return paramsFinal
}

export const getSortedParams = (params: IParams): string => {
  const sortedParams: IParams = {}

  for (const [key, value] of Object.entries(params)) {
    if (key !== 'page' && key !== 'perPage') {
      sortedParams[key] = value
    }
  }

  if (params.page) sortedParams.page = params.page

  if (params.perPage) sortedParams.perPage = params.perPage

  return queryString.stringify(sortedParams, { sort: false })
}

/** Функция получения task из конверта для DECLINE */
export const getEnvelopeTaskForDecline = (
  envelope: ICurrentEnvelope,
  currentUserBoxOguid: string,
) => {
  const { flowState, flow, allowedActions } = envelope;

  let result = null;
  if (
    flowState === EnvelopeFlowState.IN_PROGRESS &&
    (
      allowedActions.includes(EnvelopeAllovedActions.DECLINE_APPROVE)
      ||
      allowedActions.includes(EnvelopeAllovedActions.DECLINE_SIGN)
    )
  ) {
    for (let i = 0; i < flow.length; i++) {
      if (flow[i].state === FlowStageState.IN_PROGRESS) {
        for (let j = 0; j < flow[i].tasks.length; j++) {
          const item = flow[i].tasks[j];
          if (
            item.state === FlowStageState.IN_PROGRESS &&
            (item.type === TasksStatus.SIGNING ||
            item.type === TasksStatus.APPROVAL) &&
            item.recipient.boxOguid === currentUserBoxOguid
          ) {
            result = item;
            break;
          }
        }
        if (result) {
          break;
        }
      }
    }
  }

  return result;
};

/** Функция определения необходимости/наличия КЭП */
export const getEnvelopeQualifiedSignature = (
  envelope: ICurrentEnvelope,
  currentUserBoxOguid: string,
  instance: Instance,
  /** Проверка наличия КЭП в завершённой задаче */
  completed?: boolean
) => {
  const { flowState, flow, allowedActions } = envelope;

  const envelopeFlowStateCheck = completed
    ? EnvelopeFlowState.SOLVED
    : EnvelopeFlowState.IN_PROGRESS;
  const flowStateCheck = completed
    ? FlowStageState.COMPLETED
    : FlowStageState.IN_PROGRESS;

  let result = false;
  if (
    flowState === envelopeFlowStateCheck &&
    (allowedActions.includes(EnvelopeAllovedActions.SIGN) || completed) &&
    instance === Instance.RU
  ) {
    for (let i = 0; i < flow.length; i++) {
      if (flow[i].state === flowStateCheck) {
        for (let j = 0; j < flow[i].tasks.length; j++) {
          const item = flow[i].tasks[j];
          if (
            item.state === flowStateCheck &&
            item.type === TasksStatus.SIGNING &&
            item.recipient.boxOguid === currentUserBoxOguid &&
            item.recipient.signingParams.isAllowElectronicSignature === false
          ) {
            result = true;
            break;
          }
        }
        if (result) {
          break;
        }
      }
    }
  }

  return result;
};

/** Получение списка массовых операций в зависимости от роли пользователя и страницы */
export const getMassiveActions = (
  t: TFunction,
  disabled: boolean,
  role: IBoxRole,
  page?: MassiveActionPage
): ILibDropdownItem[][] => {
  const allActions = [
    [
      { name: AllowedAction.RESEND, translation: t('boxes:users.resend') },
      {
        name: AllowedAction.DELETE,
        translation: t('common:delete'),
        isDisabled: disabled,
      },
    ],
  ];

  switch (page) {
    case MassiveActionPage.INVITES: {
      const allowedActions = [AllowedAction.RESEND, AllowedAction.DELETE];

      return filterAllowedActions(allActions, allowedActions, role);
    }
    default: {
      return allActions;
    }
  }
};

/** Фильтрация списка массовых операций в зависимости от роли пользователя */
const filterAllowedActions = (
  allActions: ILibDropdownItem[][],
  allowedActions: string[],
  role: IBoxRole
): ILibDropdownItem[][] => {
  const ADMIN_ACTIONS: string[] = [AllowedAction.RESEND, AllowedAction.DELETE];

  const MANAGER_ACTIONS: string[] = [
    AllowedAction.RESEND,
    AllowedAction.DELETE,
  ];

  return allActions.map((actions) =>
    actions.filter((action) => {
      if (!allowedActions.includes(action.name)) return false;

      switch (role) {
        case IBoxRole.ADMIN: {
          if (!ADMIN_ACTIONS.includes(action.name)) return false;
          break;
        }
        case IBoxRole.MANAGER: {
          if (!MANAGER_ACTIONS.includes(action.name)) return false;
          break;
        }
        default:
          break;
      }

      return true;
    })
  );
};

/** Проверка валидности номера телефона */
export const checkValidityPhoneNumber = (value: string): boolean => {
  return window.intlTelInputUtils
    ? window.intlTelInputUtils.isValidNumber(value, '')
    : true;
};

/** Получение суффикса емейла при формировании через номер телефона */
export const getEmailSuffix = (instance: Instance) => {
  return instance === Instance.RU
    ? '@greendocs.infologistics.ru'
    : '@greendocs.infologistics.global';
};

/** Debouce function */
export function debounce<T extends (...args: any[]) => void>(fn: T, delay = 200) {
  let t: ReturnType<typeof setTimeout> | null = null;
  const wrapped = (...args: Parameters<T>) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
  wrapped.cancel = () => { if (t) clearTimeout(t); t = null; };
  return wrapped as T & { cancel: () => void };
}
